import httpClient from "@/api/httpClient.ts";
import { AuthContextProps } from "@/providers/AuthProvider";
import ConfigService from "@/services/ConfigService.ts";
import { toCasinoBadgePromoConfig } from "@/modules/badgesModule/casinoBadgeMapper";
import { useQuery, useMutation, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

type QueryOptions = {
  enabled?: boolean;
};

const USER_PROGRESS_STALE_TIME = 15 * 1000;

const resolvePromoId = (promoRef?: string) => promoRef || ConfigService.getPromoId();
const selectData = <T>(response: AxiosResponse<T>) => response.data;
const getPromoQueryKey = (key: string, promoRef?: string) => [key, resolvePromoId(promoRef)] as const;
export const normalizeBadgePayloadToWidgetModel = toCasinoBadgePromoConfig;

export const useCasinoData = (promoRef?: string, options?: QueryOptions) => {
  const promoId = resolvePromoId(promoRef);
  return useQuery({
    queryKey: ["casinoData", promoId],
    queryFn: () => httpClient.get(`/casino/${promoId}`),
    select: selectData,
    enabled: (options?.enabled ?? true) && !!promoId,
  });
};

export const useBadgeEndlessData = (context: AuthContextProps, options?: QueryOptions) => {
  return useQuery({
    queryKey: ["badgeEndlessData", context.isLoggedIn],
    queryFn: () => httpClient.get(`/badge/lobby/organization`),
    select: (response) => toCasinoBadgePromoConfig(response.data),
    enabled: options?.enabled ?? true,
  });
};

export const useOptInStatus = (
  _context: AuthContextProps,
  condition: boolean,
  promoRef?: string
) => {
  const promoId = resolvePromoId(promoRef);

  return useQuery({
    queryKey: getPromoQueryKey("optInStatus", promoRef),
    queryFn: () => httpClient.get(`/opt-in/${promoId}`),
    select: selectData,
    enabled: !!condition,
  });
};

export const useOptIn = (_context: AuthContextProps, promoRef?: string) => {
  const promoId = resolvePromoId(promoRef);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => httpClient.post(`/opt-in/${promoId}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getPromoQueryKey("optInStatus", promoRef),
      });
      queryClient.invalidateQueries({
        queryKey: getPromoQueryKey("userProgress", promoRef),
      });
    },
  });
};

export const useUserProgress = (
  _context: AuthContextProps,
  isLoggedIn: boolean,
  promoRef?: string
): UseQueryResult<any, Error> & { enabled: boolean } => {
  const promoId = resolvePromoId(promoRef);
  const enabled = isLoggedIn === true;

  return {
    ...useQuery({
      queryKey: getPromoQueryKey("userProgress", promoRef),
      queryFn: async () => {
        const response = await httpClient.get(`/casino/${promoId}/user/progress`);
        return response.data;
      },
      enabled,
      staleTime: USER_PROGRESS_STALE_TIME,
    }),
    enabled,
  };
};

export const useUserSpins = (
  _context: AuthContextProps,
  isLoggedIn: boolean
): UseQueryResult<any, Error> & { enabled: boolean } => {
  const promoId = ConfigService.getPromoId();
  const enabled = isLoggedIn === true;

  return {
    ...useQuery({
      queryKey: ["userSpins", promoId],
      queryFn: () => httpClient.get(`/casino/${promoId}/spin`),
      select: selectData,
      enabled,
    }),
    enabled,
  };
};

interface UseUserSpinsVariables {
  onSuccess?: (data: AxiosResponse<any, any>) => void;
  onError?: (error: Error) => void;
}

export const useUserSpinsMutation = (_context: AuthContextProps) => {
  const promoId = ConfigService.getPromoId();

  return useMutation<AxiosResponse<any>, Error, void, UseUserSpinsVariables>({
    mutationFn: () => httpClient.post(`/casino/${promoId}/spin`),
    onSuccess: (data, variables: any) => {
      variables?.onSuccess?.(data); // Call the optional onSuccess callback
      // console.log("Default onSuccess: Post request successful:", data);
    },
    onError: (error, variables: any) => {
      variables?.onError?.(error); // Call the optional onError callback
      // console.error("Default onError: Post request failed:", error);
    },
  });
};
